



































































































import { Component, Inject, Prop, Vue, Watch } from 'vue-property-decorator'
import { RealworksStatusRule } from '@/api/model/realworks-status-rule'
import { cloneDeep, isEqual } from 'lodash'
import { generateReferralStatusOptions, InputSelectOption } from '@/helpers/options'
import { Pipeline } from '@/api/model/pipeline'
import { PipelineAPI } from '@/api/backend-client'
import { Status } from '@/api/model/status'
import { StatusAPI } from '@/api/backend-client'
import { StatusReason } from '@/api/model/status-reason'
import { StatusReasonAPI } from '@/api/backend-client'
import { allowedReferralStatusReasons, ReferralStatus, ReferralType } from '@/api/types'

@Component
export default class CreatePhaseStatusFormStep2 extends Vue {
    @Prop({ required: true }) readonly value!: RealworksStatusRule
    editingValue: RealworksStatusRule = null
    loadingPipelines = true
    pipelines: Pipeline[] = []
    loadingNewStatuses: boolean = true
    newStatuses: Status[] = []
    loadingNewStatusReasons: boolean = true
    newStatusReasons: StatusReason[] = []
    @Inject() kenmerkLabel: (kenmerkId) => string

    mounted() {
        this.loadPipelines()
        this.loadNewStatuses()
        this.loadNewStatusReasons()
    }

    @Watch('value', { immediate: true })
    onValueChanged() {
        this.editingValue = cloneDeep(this.value)
    }

    @Watch('editingValue.pipeline')
    @Watch('editingValue.phases')
    @Watch('editingValue.toStatus')
    @Watch('editingValue.toPhase')
    @Watch('editingValue.toNewStatusReason')
    onEditingValueChanged() {
        if (isEqual(this.editingValue, this.value)) {
            return
        }

        this.$emit('update:value', this.editingValue)
    }

    async loadPipelines(): Promise<void> {
        this.loadingPipelines = true
        this.pipelines = await PipelineAPI.getAll({ company: this.$currentCompany.id })
        this.loadingPipelines = false
    }

    async loadNewStatuses(): Promise<void> {
        this.loadingNewStatuses = true
        this.newStatuses = await StatusAPI.getAll({ company: this.$currentCompany.id })
        this.loadingNewStatuses = false
    }

    async loadNewStatusReasons(): Promise<void> {
        this.loadingNewStatusReasons = true
        this.newStatusReasons = await StatusReasonAPI.getAll({ company: this.$currentCompany.id })
        this.loadingNewStatusReasons = false
    }

    get pipelineOptions(): Pipeline[] {
        return this.pipelines
            .filter(pipeline => pipeline.active)
            .sort((a, b) => a.name.localeCompare(b.name))
    }

    get phases(): string[] {
        const phases = this.value?.phases || []

        return phases.map(phase => phase.id)
    }

    set phases(ids: string[]) {
        const phases = this.editingValue.pipeline?.phases || []

        this.editingValue.phases = phases.filter(phase => ids.includes(phase.id))
    }

    get phasesOptions(): InputSelectOption[] {
        const phases = this.editingValue.pipeline?.phases || []

        return phases
            .map(phase => ({
                label: phase.name,
                value: phase.id,
            }))
    }

    get toPhase(): string|null {
        return this.editingValue?.toPhase?.id || null
    }

    set toPhase(id: string|null) {
        const phases = this.editingValue?.pipeline?.phases || []

        this.editingValue.toPhase = phases
            .find(v => v.id === id) || null
    }

    get toStatuses() {
        return generateReferralStatusOptions(this.$currentCompany)
    }

    get toStatusReasons() {
        if (!this.editingValue.toStatus || this.$currentCompany.pipelinesEnabled) {
            return []
        }

        const allowedStatusReasons = allowedReferralStatusReasons(
            'all' as ReferralType,
            this.editingValue.toStatus as ReferralStatus,
        )

        return allowedStatusReasons.map(statusReason => {
            return {
                label: this.$t(`referral.statusReason.${statusReason}`),
                value: statusReason,
            }
        })
    }

    get toNewStatus(): Status|null {
        if (!this.editingValue || !this.editingValue.toStatus || !this.$currentCompany.pipelinesEnabled) {
            return null
        }

        return this.newStatuses.find((status: Status) => status.status === this.editingValue.toStatus)
    }

    get toNewStatusReasons(): StatusReason[] {
        if (!this.editingValue || !this.editingValue.toStatus || !this.$currentCompany.pipelinesEnabled) {
            return []
        }

        return this.newStatusReasons.filter((statusReason: StatusReason) => statusReason.status === this.editingValue.toStatus)
    }

    get toNewStatusReason(): string|null {
        return this.editingValue?.toNewStatusReason?.id || null
    }

    set toNewStatusReason(id: string|null) {
        this.editingValue.toNewStatusReason = this.toNewStatusReasons
            .find(v => v.id === id) || null
    }

    get pipeline(): string|null {
        return this.editingValue?.pipeline?.id || null
    }

    get toNewStatusReasonOptions(): InputSelectOption[] {
        if (!this.editingValue?.toStatus || !this.editingValue?.pipeline) {
            return []
        }

        let options = this.toNewStatusReasons
            .filter(statusReason => statusReason.status === this.editingValue.toStatus)
            .filter(statusReason => statusReason.pipeline.id === this.pipeline)

        if (this.editingValue.toStatus === 'open') {

            if (!this.editingValue.toPhase) {
                return []
            }

            options = options
                .filter(statusReason => {
                    return statusReason
                        .phases
                        .map(phase => phase.id)
                        .includes(this.editingValue.toPhase.id)
                })
        }

        return options.map(statusReason => ({
            label: statusReason.name,
            value: statusReason.id,
        }))
    }

    get showPickPhaseReason(): boolean {
        return ['open'].includes(this.toNewStatus.status)
            && this.toNewStatusReasonOptions.length > 0
    }

    get showPickStatusReason(): boolean {
        return ['successful', 'closed'].includes(this.toNewStatus.status)
            && this.toNewStatusReasonOptions.length > 0
    }

    get isPhaseReasonRequired(): boolean {
        return this.showPickPhaseReason
            && this.editingValue.toPhase?.statusReasonRequired
    }

    get isStatusReasonRequired(): boolean {
        return this.showPickStatusReason
            && this.toNewStatus.statusReasonRequired
    }

    get phaseLabel() {
        if (this.value.pipeline) {
            return this.$t('virtualColleague.phaseStatus.phaseLabel', {pipeline: this.value.pipeline.name})
        } else {
            return this.$t('virtualColleague.phaseStatus.phaseLabelEmpty')
        }
    }

    get avatarHeaderText() {
        const values = {
            kenmerken: this.value.kenmerken.map(kenmerkId => this.kenmerkLabel(kenmerkId)).join(', '),
            agendaTypes: this.value.agendaTypes.join(', '),
            agendaStatus: this.value.agendaStatus,
            taakTypes: this.value.taakTypes.join(', '),
            taakStatus: this.value.taakStatus,
        }

        if (this.value.triggerType === 'kenmerken') {
            return this.$t('virtualColleague.phaseStatus.avatarHeader.stepTwo.kenmerken', values)
        } else if (this.value.triggerType === 'agendaTypes') {
            if (this.value.agendaStatus) {
                return this.$t('virtualColleague.phaseStatus.avatarHeader.stepTwo.agendaTypesWithStatus', values)
            }

            return this.$t('virtualColleague.phaseStatus.avatarHeader.stepTwo.agendaTypes', values)
        } else {
            if (this.value.taakStatus) {
                return this.$t('virtualColleague.phaseStatus.avatarHeader.stepTwo.taakTypesWithStatus', values)
            }

            return this.$t('virtualColleague.phaseStatus.avatarHeader.stepTwo.taakTypes', values)
        }
    }
}
